import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useToast } from '@/hooks/use-toast';
import { TagItem, fetchTags, getCachedTags, tagsStore } from '@/lib/stores/tagsStore';

interface TagsDataContextType {
  tags: TagItem[];
  isLoading: boolean;
  error: string | null;
  refreshTags: () => Promise<void>;
}

const TagsDataContext = createContext<TagsDataContextType | undefined>(undefined);

export function TagsDataProvider({ children }: { children: React.ReactNode }) {
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [tags, setTags] = useState<TagItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTags = async () => {
    if (!selectedGame) {
      setTags([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First try to get cached data
      const cachedTags = getCachedTags(selectedGame.id);
      if (cachedTags.length > 0) {
        setTags(cachedTags);
        setIsLoading(false);
        return;
      }

      // If no cached data, fetch from API
      const fetchedTags = await fetchTags(selectedGame.id, toast);
      setTags(fetchedTags);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load tags';
      setError(message);
      console.error('Error loading tags:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTags = async () => {
    await loadTags();
  };

  // Load tags when selected game changes
  useEffect(() => {
    loadTags();
  }, [selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!selectedGame?.id) return;

    const unsubscribe = tagsStore.subscribe(() => {
      // Get the latest data from the store and filter by current game
      const allTags = tagsStore.getData();
      const gameTags = allTags.filter(tag => tag.game_id === selectedGame.id);
      setTags(gameTags);
    });

    return () => unsubscribe();
  }, [selectedGame?.id]);

  const value: TagsDataContextType = {
    tags,
    isLoading,
    error,
    refreshTags,
  };

  return (
    <TagsDataContext.Provider value={value}>
      {children}
    </TagsDataContext.Provider>
  );
}

export function useTagsDataContext() {
  const context = useContext(TagsDataContext);
  if (context === undefined) {
    throw new Error('useTagsDataContext must be used within a TagsDataProvider');
  }
  return context;
} 